/**
 * helpers.ts — утилиты, scroll-lock, поиск
 * Проект: TorgiRT (shared — все витрины)
 */

import type { Product, ProductRaw, CatalogData } from './types';
import { state, BASE_PATH } from './state';

/* ── Scroll-lock for modals (iOS / Telegram WebView safe) ── */

let _scrollLockY = 0;

export function lockBodyScroll(): void {
  if (document.body.style.position === "fixed") return;
  _scrollLockY = window.scrollY;
  document.body.style.position = "fixed";
  document.body.style.top = `-${_scrollLockY}px`;
  document.body.style.left = "0";
  document.body.style.right = "0";
  document.body.style.overflow = "hidden";
}

export function unlockBodyScroll(): void {
  if (document.body.style.position !== "fixed") return;
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.overflow = "";
  window.scrollTo(0, _scrollLockY);
  const drawer = document.getElementById("mobile-drawer");
  if (drawer && drawer.classList.contains("is-open")) {
    document.body.style.overflow = "hidden";
  }
}

/* ── Helpers ── */

import { getConfig } from './config';

export { getConfig };

export function normalizeProduct(product: ProductRaw): Product {
  return {
    id: Number(product.id ?? 0) || undefined,
    article: String(product.article ?? product.a ?? ""),
    name: String(product.name ?? product.n ?? ""),
    price: Number(product.price ?? product.p ?? 0),
    unit: String(product.unit ?? product.u ?? "шт."),
    stock: Number(product.stock ?? product.s ?? 0),
    images: Array.isArray(product.images) ? product.images : Array.isArray(product.i) ? product.i : [],
    filters: product.filters ?? product.f ?? {},
    br: Number(product.br ?? product.baseReward ?? 0),
    badges: Array.isArray(product.badges) ? product.badges : Array.isArray(product.bd) ? product.bd : [],
    oldPrice: product.oldPrice ?? product.op ?? null,
    avgRating: Number(product.avgRating ?? product.ar ?? 0),
    reviewCount: Number(product.reviewCount ?? product.rc ?? 0),
  };
}

/**
 * Распаковать каталог из коротких ключей (оптимизированный JSON) в длинные.
 */
export function unpackCatalog(catalogData: CatalogData & Record<string, unknown>): CatalogData {
  if (!catalogData || !Array.isArray(catalogData.categories)) return catalogData;
  for (const cat of catalogData.categories) {
    const c = cat as any;
    if (c.n !== undefined && c.name === undefined) { c.name = c.n; delete c.n; }
    if (c.f !== undefined && c.filters === undefined) { c.filters = c.f; delete c.f; }
    if (c.sc !== undefined && c.subcategories === undefined) { c.subcategories = c.sc; delete c.sc; }
    if (!Array.isArray(cat.subcategories)) (cat as any).subcategories = [];
    for (const sub of cat.subcategories) {
      const s = sub as any;
      if (s.n !== undefined && s.name === undefined) { s.name = s.n; delete s.n; }
      if (s.pr !== undefined && s.products === undefined) { s.products = s.pr; delete s.pr; }
      if (!Array.isArray(sub.products)) (sub as any).products = [];
    }
  }
  return catalogData;
}

export function escapeHtml(value: string | number): string {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export function formatPrice(value: number | string): string {
  const parts = Number(value).toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
  return parts.join('.');
}

export function formatMoney(value: number | string): string {
  return formatPrice(value) + '\u00A0₽';
}

export const SF_CURRENCY_HTML = '<span class="sf-currency">\u00A0₽</span>';

export function formatMoneyHtml(value: number | string): string {
  return formatPrice(value) + SF_CURRENCY_HTML;
}

export function getCartTotal(): number {
  return state.cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

export function getCartUnitsCount(): number {
  return state.cart.reduce((sum, item) => sum + item.quantity, 0);
}

export function getCartQuantity(article: string): number {
  const item = state.cart.find((cartItem) => cartItem.article === article);
  return item ? item.quantity : 0;
}

export function isCartAllowedPage(): boolean {
  const path = window.location.pathname.replace(/\/+$/, "");
  const base = BASE_PATH.replace(/\/+$/, "");

  if (path === base || path === base + "/search" || path === base + "/zakaz"
      || path.startsWith(base + "/product/")) return true;

  const refCode = (window.CART_CONFIG || {} as Record<string, string>).refCode || '';
  if (refCode) {
    const baseNoRef = base.replace('/' + refCode, '');
    if (path === baseNoRef || path === baseNoRef + "/search" || path === baseNoRef + "/zakaz"
        || path.startsWith(baseNoRef + "/product/")) return true;
  }

  return false;
}

export function isOnZakazPage(): boolean {
  return window.location.pathname.replace(/\/+$/, "").endsWith("/zakaz");
}

/** Catalog listing: `/` or `/search` only; not `/product/` (state.productPage guard). */
export function isCatalogListingPage(): boolean {
  if (state.productPage) return false;
  const path = window.location.pathname.replace(/\/+$/, "");
  const base = BASE_PATH.replace(/\/+$/, "");
  return path === base || path === base + "/search";
}

/** Resolve numeric dbId for ?cat= redirect before state is set (canon: url.ts getUrlCatDbId). */
export function resolveCatalogCatDbId(categoryId: string, subcategoryId?: string): number | null {
  const cat = state.catalogData.categories.find((c) => c.id === categoryId);
  if (!cat) return null;
  if (subcategoryId) {
    const sub = cat.subcategories.find((s) => s.id === subcategoryId);
    if (sub && sub.dbId != null && sub.dbId !== 0) return sub.dbId;
  }
  return cat.dbId ?? null;
}

/** Full navigation to catalog with category filter (DES-S-06 H3). */
export function navigateToCatalogCat(dbId: number): void {
  window.location.href = BASE_PATH + "?cat=" + encodeURIComponent(String(dbId));
}

export function updateDocumentTitle(): void {
  const storeName = getConfig('storeName', 'Магазин') as string;
  if (state.productPage) {
    document.title = state.productPage.name + ' — ' + storeName;
    return;
  }
  if (!isCatalogListingPage()) {
    return;
  }
  const q = state.searchQuery.trim();
  if (q) {
    document.title = 'Поиск: ' + q + ' — ' + storeName;
    return;
  }
  const path = state.breadcrumbPath;
  if (path) {
    const leaf = (path.subcategoryName || path.categoryName || '').trim();
    if (leaf) {
      document.title = leaf + ' — купить в ' + storeName;
      return;
    }
  }
  document.title = 'Каталог товаров — ' + storeName;
}

/**
 * Расстояние Дамерау-Левенштейна между двумя строками.
 */
export function levenshtein(a: string, b: string, maxDist: number = 2): number {
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > maxDist) return Infinity;
  if (a === b) return 0;
  if (la === 0) return lb;
  if (lb === 0) return la;

  const d: number[][] = [];
  for (let i = 0; i <= la; i++) {
    d[i] = [i];
  }
  for (let j = 0; j <= lb; j++) {
    d[0][j] = j;
  }

  for (let i = 1; i <= la; i++) {
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + cost);
      }
    }
  }
  return d[la][lb];
}

/**
 * Формирование WebP-URL из оригинального URL (H.7).
 */
export function toWebpSrc(url: string): string | null {
  if (!url || url.endsWith('.webp')) return null;
  return url.replace(/\.(jpe?g|png)$/i, '.webp');
}

/** Сравнить currentSrc (может быть .webp) с путём из API (.png/.jpg). */
export function imageSrcMatches(currentSrc: string, imagePath: string): boolean {
  if (!currentSrc || !imagePath) return false;
  if (currentSrc.endsWith(imagePath) || currentSrc === imagePath) return true;
  const stripExt = (s: string) => s.replace(/\.(jpe?g|png|webp)$/i, '');
  const pathNoExt = stripExt(imagePath);
  try {
    const pathname = new URL(currentSrc, typeof location !== 'undefined' ? location.href : 'http://local/').pathname;
    const currentNoExt = stripExt(pathname);
    return currentNoExt === pathNoExt || currentNoExt.endsWith(pathNoExt);
  } catch {
    return stripExt(currentSrc).endsWith(pathNoExt);
  }
}

/** Обновить <img> и соседний <source type="image/webp"> внутри <picture>. */
export function setPictureImageSrc(img: HTMLImageElement, src: string, alt?: string): void {
  if (alt !== undefined) img.alt = alt;
  img.src = src;
  const picture = img.closest('picture');
  if (!picture) return;
  let source = picture.querySelector('source[type="image/webp"]') as HTMLSourceElement | null;
  const webp = toWebpSrc(src);
  if (webp) {
    if (!source) {
      source = document.createElement('source');
      source.type = 'image/webp';
      picture.insertBefore(source, img);
    }
    source.srcset = webp;
  } else if (source) {
    source.remove();
  }
}

/**
 * Поиск товаров по массиву с разбиением запроса на слова.
 */
export function searchProducts(products: Product[], query: string, mode?: string): Product[] {
  if (!query.trim()) return products;
  const words = query.trim().toLowerCase().split(/\s+/).filter(function (w) { return w.length > 1; });
  if (words.length === 0) return products;
  const check = mode === "partial"
    ? function (text: string) { return words.some(function (word) { return text.includes(word); }); }
    : function (text: string) { return words.every(function (word) { return text.includes(word); }); };
  return products.filter(function (product) {
    const text = (product.name + " " + product.article).toLowerCase();
    return check(text);
  });
}

/** Уважение системной настройки «уменьшить движение». */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function scrollBehavior(): ScrollBehavior {
  return prefersReducedMotion() ? 'auto' : 'smooth';
}
